import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpItemService from "@plannel/services/fp/fp-item-service";
import ItemGroupPopup from "@plannel/pages/factory-plan/common/popup/ItemGroupPopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import ColorPickerRenderer from "@plannel/pages/factory-plan/common/component/ColorPickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import Button from "@mui/material/Button";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 2개(itemCd/itemGrpCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "itemCd", label: "FP_ITEM_CD" },
  { field: "itemGrpCd", label: "FP_ITEM_GRP_CD" }
];

// TODO(MIG-T2): [item/Item] 그리드 건수 상태표시(GridCnt, ItemTab/InventoryTab 공통) 미이관.
//   old: <StatusArea><GridCnt grid="..." format="{0} 건 ..."/></StatusArea>.
//   사유: old도 show={false}로 실제 노출 안 되던 장식성 상태표시. golden reference(Route.js)도 미구현.

/**
 * 품목(Item) 탭 — old ItemTab.jsx 대응. §11-1(항상 마운트 + display 토글)로 Item.js에서
 * 렌더링되므로, planScope prop이 바뀔 때마다 이 탭도 자체적으로 재조회한다(탭이 비활성이어도
 * 마운트 상태 유지 — 탭 전환 자체는 재조회를 트리거하지 않음, §11-3).
 * searchFields는 Item.js가 소유한 전체 필드 중 이 탭이 쓰는 부분(itemGrpCdNm/itemCdNm)만 클라이언트
 * 필터에 사용(§6-1 케이스3).
 *
 * props: t, viewName(그리드 p13n 키), planScope, searchFields
 */
const ItemTab = ({ t, viewName, planScope, searchFields }) => {
  const gridRef = useRef();
  const [item, setItem] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [itemGroupPopup, setItemGroupPopup] = useState({ open: false, node: null, editable: false });

  const itemClassCdValuesRef = useCommonCodeValues("FP_ITEM_CLASS_CD");
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_ITEM"), viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // planScope는 서버 조회, itemGrpCdNm/itemCdNm은 AG Grid 외부 필터로 즉시 필터(§6-1 케이스3).
  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { itemGrpCdNm, itemCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (itemGrpCdNm && !(hit(r.itemGrpCd, itemGrpCdNm) || hit(r.itemGrpNm, itemGrpCdNm))) return false;
    if (itemCdNm && !(hit(r.itemCd, itemCdNm) || hit(r.itemNm, itemCdNm))) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const columnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old setCodeColumnStyle 대응 — 신규 행(id 없음)만 코드 편집 가능, 저장된 행은 PK성 코드 잠금.
    { headerName: "FP_ITEM_CD", field: "itemCd", colId: "itemCd", cellClass: "stringType", width: 180,
      editable: params => !params.data?.id },
    { headerName: "FP_ITEM_NM", field: "itemNm", colId: "itemNm", cellClass: "stringType", width: 240 },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", cellClass: "stringType", width: 110 },
    { headerName: "FP_ITEM_CLASS_CD", field: "itemClassCd", colId: "itemClassCd", cellClass: "stringType", width: 110,
      type: ["centerAligned"], cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_ITEM_CLASS_CD", itemClassCdValuesRef, t) },
    { headerName: "FP_ITEM_UOM", field: "itemUom", colId: "itemUom", cellClass: "stringType", width: 110, type: ["centerAligned"] },
    { headerName: "FP_PRIORITY", field: "priority", colId: "priority", width: 110, type: ["numberColumn", "rightAligned"] },
    // old setColorPickerRenderer(gridView, 'displayColor') 대응 — ColorPickerRenderer로 이관
    // 완료(§8-1, Resource.js와 동일 패턴). hue 슬라이더/swatch 프리셋만 Tier2 유보(§8-1 참고).
    { headerName: "FP_DISPLAY_COLOR", field: "displayColor", colId: "displayColor", width: 110,
      editable: false, cellRenderer: ColorPickerRenderer, cellStyle: { padding: 0 } },
    { headerName: "FP_SOL", field: "npi", colId: "npi", width: 130, type: ["centerAligned", "dateColumn"] },
    { headerName: "FP_EOL", field: "eol", colId: "eol", width: 130, type: ["centerAligned", "dateColumn"] },
    { headerName: "FP_ITEM_GRP_CD", field: "itemGrpCd", colId: "itemGrpCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setItemGroupPopup({ open: true, node, editable: false }) } },
    { headerName: "FP_ITEM_GRP_NM", field: "itemGrpNm", colId: "itemGrpNm", type: ["nonEditableColumn"], width: 240 },
    // 작업주문 수량(그룹) — 원자재(M)는 old itemClassTypeStyleCallBack과 동일하게 비편집.
    {
      headerName: "FP_ORDER_QTY", groupId: "orderQty", marryChildren: true, openByDefault: true,
      children: [
        { headerName: "FP_WO_SIZE_MIN", field: "woSizeMin", colId: "woSizeMin", width: 110, type: ["numberColumn", "rightAligned"],
          editable: (params) => params.data?.itemClassCd !== "M" },
        { headerName: "FP_WO_SIZE_MAX", field: "woSizeMax", colId: "woSizeMax", width: 110, type: ["numberColumn", "rightAligned"],
          editable: (params) => params.data?.itemClassCd !== "M" },
        { headerName: "FP_WO_SIZE_MULTIPLR", field: "woSizeMultiplr", colId: "woSizeMultiplr", width: 110, type: ["numberColumn", "rightAligned"],
          editable: (params) => params.data?.itemClassCd !== "M" }
      ]
    },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveItem = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpItemService.getItems({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setItem([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setItem(results);
        }
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) {
          gridRef.current?.api?.setGridOption("loading", false);
        }
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    retrieveItem();
  };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, itemCd: "", itemNm: "", descTxt: "", itemClassCd: "M", itemUom: "",
      priority: 0, displayColor: "#ff96c8", npi: null, eol: null, itemGrpCd: "", itemGrpNm: "",
      woSizeMin: 0, woSizeMax: 0, woSizeMultiplr: 0
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeItem = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpItemService.removeItems(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveItem());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveItem = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpItemService.upsertItems(allStateData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveItem());
  };

  const onSelectItemGroup = (row) => {
    const node = itemGroupPopup.node;
    if (node) {
      node.setDataValue("itemGrpCd", row.itemGrpCd);
      node.setDataValue("itemGrpNm", row.itemGrpNm);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveItem();
    else if (action === "DELETE") removeItem();
  };

  // 화면 내부 탭(§11-3): planScope가 바뀔 때만 재조회 — 탭 전환 자체는 재조회를 트리거하지 않음.
  useEffect(() => {
    if (!gridRef.current?.api) return;
    retrieveItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Box sx={{ display: "flex" }}>
          <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t("FP_ITEM")}.xlsx`, sheetName: t("FP_ITEM") })} />
          <Button size="small" onClick={() => setItemGroupPopup({ open: true, node: null, editable: true })}>
            {t("FP_ITEM_GROUP_MST")}
          </Button>
        </Box>
        <Box sx={{ display: "flex" }}>
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
        </Box>
      </Box>
      <Box id="fp-item-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={item}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <ItemGroupPopup
        open={itemGroupPopup.open}
        onClose={() => setItemGroupPopup({ open: false, node: null, editable: false })}
        planScope={planScope}
        onSelect={onSelectItemGroup}
        editable={itemGroupPopup.editable}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ItemTab);
